import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, BarChart3, Wallet, PiggyBank, CreditCard, ArrowRight, PlayCircle, FileSpreadsheet, ShieldCheck, TrendingUp, Layout, Star, Sparkles, MessageCircle } from 'lucide-react';
import { MetaPixel } from './components/MetaPixel';
import { PurchasePopup } from './components/PurchasePopup';
import { CountdownTimer } from './components/CountdownTimer';
import { WhatsAppButton } from './components/WhatsAppButton';
// Images
const IMAGES = {
  hero: 'https://cdn.magicpatterns.com/uploads/cWWbwsuRTvDFM7hZkfba3N/Diseno_sin_titulo_(7).png',
  planner: 'https://cdn.magicpatterns.com/uploads/fVaV3UyKxzZmnCvaiVnikC/planner.png',
  tutorial: 'https://cdn.magicpatterns.com/uploads/hqKvFut7oTwmVbz4AEPZMG/tutorial.png'
};
const CHECKOUT_URL = 'https://pay.hotmart.com/R103783715V';
export function App() {
  const scrollToPricing = () => {
    const element = document.getElementById('precio');
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  return <div className="min-h-screen bg-slate-50 font-sans text-slate-900 overflow-x-hidden" data-id="element-0">
      <MetaPixel data-id="element-1" />
      <PurchasePopup data-id="element-2" />
      <WhatsAppButton data-id="element-3" />

      {/* Urgency Banner */}
      <div className="bg-gradient-to-r from-teal-600 to-teal-700 text-white py-3 px-4 text-center sticky top-0 z-50 shadow-lg" data-id="element-4">
        <div className="max-w-7xl mx-auto flex items-center justify-center gap-2 flex-wrap" data-id="element-5">
          <Sparkles className="w-5 h-5 animate-pulse" data-id="element-6" />
          <span className="font-bold text-sm md:text-base" data-id="element-7">
            🔥 Oferta por tiempo limitado: 60% de descuento
          </span>
          <span className="hidden sm:inline text-teal-100 text-sm" data-id="element-8">
            • Solo quedan pocas unidades al precio especial
          </span>
        </div>
      </div>

      {/* Hero Section */}
      <header className="relative bg-white overflow-hidden" data-id="element-9">
        <div className="absolute inset-0 bg-gradient-to-br from-teal-50 to-white z-0" data-id="element-10" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16 relative z-10" data-id="element-11">
          <div className="max-w-4xl mx-auto" data-id="element-12">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} data-id="element-13">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-teal-600 text-white text-sm font-bold mb-6" data-id="element-14">
                <FileSpreadsheet className="w-4 h-4" data-id="element-15" />
                Plantilla de Finanzas Personales
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 mb-6 leading-tight text-center" data-id="element-16">
                El control de tu dinero es el primer paso para construir una
                vida mejor.
              </h1>
              <p className="text-lg md:text-xl text-slate-600 mb-6 leading-relaxed text-center" data-id="element-17">
                Organizar tus finanzas te permite tomar mejores decisiones y
                avanzar con tranquilidad.
              </p>
              <p className="text-lg md:text-xl text-slate-600 mb-8 leading-relaxed text-center" data-id="element-18">
                Esta plantilla en Google Sheets te ayuda a ordenar ingresos,
                gastos, ahorros y deudas de forma simple, clara y sin
                complicaciones.
              </p>

              <ul className="space-y-4 mb-10 max-w-md mx-auto" data-id="element-19">
                {['Entendé en qué se va tu dinero', 'Organizá tus deudas con tranquilidad', 'Empezá a ahorrar con un plan real', 'Todo en un solo lugar'].map((item, i) => <motion.li key={i} initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 0.2 + i * 0.1
              }} className="flex items-center gap-3 text-slate-700" data-id="element-20">
                    <CheckCircle2 className="w-5 h-5 text-teal-500 flex-shrink-0" data-id="element-21" />
                    {item}
                  </motion.li>)}
              </ul>

              <div className="text-center" data-id="element-22">
                <button onClick={scrollToPricing} className="inline-flex items-center justify-center gap-2 bg-teal-600 hover:bg-teal-700 text-white font-semibold px-8 py-4 rounded-xl text-lg transition-all shadow-lg hover:shadow-xl hover:-translate-y-1" data-id="element-23">
                  Empezar a ordenar mis finanzas
                  <ArrowRight className="w-5 h-5" data-id="element-24" />
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </header>

      {/* What's Included Section */}
      <section className="py-20 bg-white" data-id="element-25">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-26">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-27">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-28">
              ¿Qué incluye la Plantilla de Finanzas Personales?
            </h2>
            <p className="text-lg text-slate-600" data-id="element-29">
              Un sistema simple para ordenar tu dinero, salir de deudas y
              empezar a ahorrar sin complicaciones.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12" data-id="element-30">
            {/* Card 1 */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="bg-white rounded-2xl shadow-lg border border-slate-100 overflow-hidden hover:shadow-xl transition-shadow" data-id="element-31">
              <div className="relative h-48 overflow-hidden" data-id="element-32">
                <img src="https://cdn.magicpatterns.com/uploads/seRP5UENPaMse8mQkNLWKR/presupuesto_mensual.png" alt="Presupuesto Mensual" className="w-full h-full object-cover" data-id="element-33" />
              </div>
              <div className="p-6" data-id="element-34">
                <div className="flex items-center gap-2 mb-3" data-id="element-35">
                  <BarChart3 className="w-5 h-5 text-teal-600" data-id="element-36" />
                  <h3 className="text-xl font-bold text-slate-900" data-id="element-37">
                    📊 Presupuesto Mensual
                  </h3>
                </div>
                <p className="text-slate-600 leading-relaxed" data-id="element-38">
                  Registrá todos tus ingresos y gastos de forma simple. Vas a
                  ver exactamente en qué se te va el dinero y dónde podés
                  recortar para llegar a fin de mes con más tranquilidad.
                </p>
              </div>
            </motion.div>

            {/* Card 2 */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="bg-white rounded-2xl shadow-lg border border-slate-100 overflow-hidden hover:shadow-xl transition-shadow" data-id="element-39">
              <div className="relative h-48 overflow-hidden" data-id="element-40">
                <img src="https://cdn.magicpatterns.com/uploads/p9kvdThhBfHRJgLUJBgYuY/ahorros.png" alt="Plan de Ahorros" className="w-full h-full object-cover" data-id="element-41" />
              </div>
              <div className="p-6" data-id="element-42">
                <div className="flex items-center gap-2 mb-3" data-id="element-43">
                  <PiggyBank className="w-5 h-5 text-emerald-600" data-id="element-44" />
                  <h3 className="text-xl font-bold text-slate-900" data-id="element-45">
                    💰 Plan de Ahorros
                  </h3>
                </div>
                <p className="text-slate-600 leading-relaxed" data-id="element-46">
                  Definí tus objetivos de ahorro, establecé plazos y seguí tu
                  progreso mes a mes. Ideal para ahorrar para viajes,
                  emergencias o metas personales sin perder motivación.
                </p>
              </div>
            </motion.div>

            {/* Card 3 */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} className="bg-white rounded-2xl shadow-lg border border-slate-100 overflow-hidden hover:shadow-xl transition-shadow" data-id="element-47">
              <div className="relative h-48 overflow-hidden" data-id="element-48">
                <img src="https://cdn.magicpatterns.com/uploads/4BFEdZWg5H4c3mgkyypLYE/deudas.png" alt="Gestión de Deudas" className="w-full h-full object-cover" data-id="element-49" />
              </div>
              <div className="p-6" data-id="element-50">
                <div className="flex items-center gap-2 mb-3" data-id="element-51">
                  <CreditCard className="w-5 h-5 text-red-600" data-id="element-52" />
                  <h3 className="text-xl font-bold text-slate-900" data-id="element-53">
                    🧾 Gestión de Deudas
                  </h3>
                </div>
                <p className="text-slate-600 leading-relaxed" data-id="element-54">
                  Registrá todas tus deudas y planificá pagos de forma ordenada.
                  Incluye métodos como Bola de Nieve y Avalancha para salir de
                  deudas más rápido y con menos estrés.
                </p>
              </div>
            </motion.div>

            {/* Card 4 */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.3
          }} className="bg-white rounded-2xl shadow-lg border border-slate-100 overflow-hidden hover:shadow-xl transition-shadow lg:col-start-1 lg:col-end-2" data-id="element-55">
              <div className="relative h-48 overflow-hidden" data-id="element-56">
                <img src="https://cdn.magicpatterns.com/uploads/eKCacvbhtAAqCwTDkfVxHD/resumen_anual.png" alt="Resumen Anual" className="w-full h-full object-cover" data-id="element-57" />
              </div>
              <div className="p-6" data-id="element-58">
                <div className="flex items-center gap-2 mb-3" data-id="element-59">
                  <TrendingUp className="w-5 h-5 text-blue-600" data-id="element-60" />
                  <h3 className="text-xl font-bold text-slate-900" data-id="element-61">
                    📈 Resumen Anual
                  </h3>
                </div>
                <p className="text-slate-600 leading-relaxed" data-id="element-62">
                  Obtené una visión clara de todo tu año financiero. Ingresos,
                  gastos, ahorros y balances en un solo lugar para tomar mejores
                  decisiones.
                </p>
              </div>
            </motion.div>

            {/* Card 5 */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.4
          }} className="bg-white rounded-2xl shadow-lg border border-slate-100 overflow-hidden hover:shadow-xl transition-shadow lg:col-start-2 lg:col-end-4" data-id="element-63">
              <div className="relative h-48 overflow-hidden" data-id="element-64">
                <img src="https://cdn.magicpatterns.com/uploads/dMzCdXwnKRyHdQHqsmDega/Instrucciones.png" alt="Video Tutorial" className="w-full h-full object-cover" data-id="element-65" />
              </div>
              <div className="p-6" data-id="element-66">
                <div className="flex items-center gap-2 mb-3" data-id="element-67">
                  <PlayCircle className="w-5 h-5 text-purple-600" data-id="element-68" />
                  <h3 className="text-xl font-bold text-slate-900" data-id="element-69">
                    🎥 Video Tutorial Paso a Paso
                  </h3>
                </div>
                <p className="text-slate-600 leading-relaxed" data-id="element-70">
                  Incluye un video explicativo donde te muestro cómo usar cada
                  parte de la plantilla. No necesitás saber de Excel ni de
                  finanzas: lo configurás en minutos.
                </p>
              </div>
            </motion.div>
          </div>

          <div className="text-center" data-id="element-71">
            <button onClick={scrollToPricing} className="inline-flex items-center justify-center gap-2 bg-teal-600 hover:bg-teal-700 text-white font-bold px-10 py-5 rounded-xl text-xl transition-all shadow-lg hover:shadow-xl hover:-translate-y-1" data-id="element-72">
              Obtener la plantilla ahora
              <ArrowRight className="w-6 h-6" data-id="element-73" />
            </button>
          </div>
        </div>
      </section>

      {/* Pain Validation */}
      <section className="py-20 bg-white" data-id="element-74">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-75">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-76">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6" data-id="element-77">
              No es que ganes poco, es que falta claridad
            </h2>
            <p className="text-xl text-slate-600 leading-relaxed" data-id="element-78">
              Es frustrante llegar a fin de mes y preguntarse "¿en qué se me fue
              la plata?". Pagar cuentas y cuotas sin sentir que avanzás genera
              ansiedad y estrés. El problema no son siempre tus ingresos, sino
              la falta de un sistema que te permita ver la foto completa de tu
              economía.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Solution */}
      <section className="py-20 bg-slate-50" data-id="element-79">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-80">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-81">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6" data-id="element-82">
              Un sistema simple para ordenar tus finanzas
            </h2>
            <p className="text-lg text-slate-600" data-id="element-83">
              Diseñada para personas reales con gastos reales. Sin fórmulas
              complicadas ni términos técnicos. Solo ingresás tus datos y
              obtenés claridad inmediata.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-84">
            {[{
            icon: <Wallet className="w-8 h-8 text-teal-600" data-id="element-85" />,
            title: 'Registro de Ingresos',
            desc: 'Visualizá todo lo que entra a tu bolsillo en un solo lugar.'
          }, {
            icon: <CreditCard className="w-8 h-8 text-red-500" data-id="element-86" />,
            title: 'Control de Gastos',
            desc: 'Categorizá tus gastos para saber exactamente dónde recortar.'
          }, {
            icon: <PiggyBank className="w-8 h-8 text-emerald-500" data-id="element-87" />,
            title: 'Seguimiento de Ahorros',
            desc: 'Establecé metas y mirá cómo crecen tus ahorros mes a mes.'
          }, {
            icon: <TrendingUp className="w-8 h-8 text-orange-500" data-id="element-88" />,
            title: 'Gestión de Deudas',
            desc: 'Organizá montos, cuotas y visualizá tu progreso para salir de ellas.'
          }, {
            icon: <BarChart3 className="w-8 h-8 text-blue-500" data-id="element-89" />,
            title: 'Resúmenes Automáticos',
            desc: 'Gráficos claros que se actualizan solos al cargar tus datos.'
          }, {
            icon: <FileSpreadsheet className="w-8 h-8 text-purple-500" data-id="element-90" />,
            title: '100% Editable',
            desc: 'Usala en Google Sheets desde tu compu o celular. Es tuya para siempre.'
          }].map((feature, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="bg-white p-8 rounded-2xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow" data-id="element-91">
                <div className="bg-slate-50 w-16 h-16 rounded-xl flex items-center justify-center mb-6" data-id="element-92">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3" data-id="element-93">
                  {feature.title}
                </h3>
                <p className="text-slate-600" data-id="element-94">{feature.desc}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-white" data-id="element-95">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-96">
          <div className="grid lg:grid-cols-2 gap-16 items-center" data-id="element-97">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} data-id="element-98">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-8" data-id="element-99">
                Recuperá la tranquilidad financiera
              </h2>
              <div className="space-y-6" data-id="element-100">
                {[{
                title: 'Dejá de adivinar',
                desc: 'Sabé exactamente si llegás a fin de mes antes de que sea tarde.'
              }, {
                title: 'Reducí el estrés',
                desc: 'Al tener todo anotado, tu mente descansa. No más cuentas mentales.'
              }, {
                title: 'Tomá mejores decisiones',
                desc: 'Decidí compras importantes basándote en datos reales, no en sensaciones.'
              }, {
                title: 'Proyectá a futuro',
                desc: 'Empezá a construir el hábito del ahorro paso a paso.'
              }].map((benefit, i) => <div key={i} className="flex gap-4" data-id="element-101">
                    <div className="flex-shrink-0 mt-1" data-id="element-102">
                      <ShieldCheck className="w-6 h-6 text-teal-500" data-id="element-103" />
                    </div>
                    <div data-id="element-104">
                      <h3 className="text-lg font-bold text-slate-900" data-id="element-105">
                        {benefit.title}
                      </h3>
                      <p className="text-slate-600" data-id="element-106">{benefit.desc}</p>
                    </div>
                  </div>)}
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="relative hidden lg:block" data-id="element-107">
              <div className="absolute inset-0 bg-gradient-to-tr from-teal-100 to-blue-50 rounded-3xl transform rotate-3" data-id="element-108"></div>
              <div className="relative bg-white p-8 rounded-3xl shadow-xl border border-slate-100" data-id="element-109">
                <div className="space-y-4" data-id="element-110">
                  <div className="h-4 bg-slate-100 rounded w-3/4" data-id="element-111"></div>
                  <div className="h-4 bg-slate-100 rounded w-1/2" data-id="element-112"></div>
                  <div className="h-32 bg-teal-50 rounded-xl w-full flex items-center justify-center" data-id="element-113">
                    <BarChart3 className="w-16 h-16 text-teal-200" data-id="element-114" />
                  </div>
                  <div className="h-4 bg-slate-100 rounded w-full" data-id="element-115"></div>
                  <div className="h-4 bg-slate-100 rounded w-5/6" data-id="element-116"></div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Bonuses */}
      <section className="py-20 bg-slate-900 text-white" data-id="element-117">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-118">
          <div className="text-center mb-16" data-id="element-119">
            <span className="text-teal-400 font-bold tracking-wider uppercase text-sm" data-id="element-120">
              Regalos Exclusivos
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mt-2" data-id="element-121">
              Bonos incluidos con tu compra
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-12" data-id="element-122">
            {/* Bonus 1 */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="bg-slate-800 rounded-2xl overflow-hidden border border-slate-700" data-id="element-123">
              <img src={IMAGES.planner} alt="Planner Financiero" className="w-full h-64 object-cover" data-id="element-124" />
              <div className="p-8" data-id="element-125">
                <div className="inline-block bg-teal-500 text-white text-xs font-bold px-2 py-1 rounded mb-4" data-id="element-126">
                  BONUS #1
                </div>
                <h3 className="text-2xl font-bold mb-3" data-id="element-127">
                  Planner Financiero Imprimible
                </h3>
                <p className="text-slate-300" data-id="element-128">
                  Ideal para planificar tu mes en papel, anotar prioridades,
                  fechas de vencimiento y establecer tus metas financieras antes
                  de pasarlas a la planilla.
                </p>
              </div>
            </motion.div>

            {/* Bonus 2 */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} className="bg-slate-800 rounded-2xl overflow-hidden border border-slate-700" data-id="element-129">
              <img src={IMAGES.tutorial} alt="Video Tutorial" className="w-full h-64 object-cover" data-id="element-130" />
              <div className="p-8" data-id="element-131">
                <div className="inline-block bg-teal-500 text-white text-xs font-bold px-2 py-1 rounded mb-4" data-id="element-132">
                  BONUS #2
                </div>
                <h3 className="text-2xl font-bold mb-3" data-id="element-133">
                  Video Tutorial Paso a Paso
                </h3>
                <p className="text-slate-300" data-id="element-134">
                  Te explico paso a paso cómo usar la plantilla desde cero para
                  que le saques el máximo provecho.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 bg-white" data-id="element-135">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-136">
          <div className="text-center mb-16" data-id="element-137">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900" data-id="element-138">
              Cómo funciona
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8" data-id="element-139">
            {[{
            step: '01',
            title: 'Accedé a la plantilla',
            desc: 'Recibís acceso inmediato a tu correo después de la compra.'
          }, {
            step: '02',
            title: 'Cargá tus datos',
            desc: 'Ingresá tus ingresos, gastos, ahorros y deudas en las celdas indicadas.'
          }, {
            step: '03',
            title: 'Visualizá tu situación',
            desc: 'Los gráficos y resúmenes se actualizan automáticamente.'
          }].map((item, i) => <div key={i} className="relative text-center p-6" data-id="element-140">
                <div className="text-6xl font-black text-slate-100 absolute top-0 left-1/2 -translate-x-1/2 -z-10" data-id="element-141">
                  {item.step}
                </div>
                <div className="bg-teal-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6 text-teal-600 font-bold text-xl relative z-10" data-id="element-142">
                  {i + 1}
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3" data-id="element-143">
                  {item.title}
                </h3>
                <p className="text-slate-600" data-id="element-144">{item.desc}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="precio" className="py-20 bg-teal-50" data-id="element-145">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-146">
          <div className="max-w-lg mx-auto bg-white rounded-3xl shadow-xl overflow-hidden border border-teal-100" data-id="element-147">
            <div className="bg-teal-600 p-6 text-center text-white" data-id="element-148">
              <h2 className="text-2xl font-bold" data-id="element-149">Oferta Especial</h2>
              <p className="text-teal-100" data-id="element-150">Acceso inmediato y de por vida</p>
            </div>

            <div className="pt-2 pb-1" data-id="element-151">
              <img src="https://cdn.magicpatterns.com/uploads/5gvPABikmpuAkAjg5EThD3/Logos_Verduleria_y_Fruteria_Simple_Verde.png" alt="Plantilla de Finanzas Personales" className="w-full h-auto object-contain px-4" data-id="element-152" />
            </div>

            <div className="px-8 md:px-12 pt-2 pb-8 md:pb-12" data-id="element-153">
              <CountdownTimer data-id="element-154" />

              <div className="text-center my-8" data-id="element-155">
                <p className="text-slate-400 line-through text-lg mb-2" data-id="element-156">
                  Precio regular: $29.99
                </p>
                <div className="flex items-center justify-center gap-2" data-id="element-157">
                  <span className="text-5xl font-bold text-slate-900" data-id="element-158">
                    $9.90
                  </span>
                  <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-sm font-bold" data-id="element-159">
                    -60%
                  </span>
                </div>
                <p className="text-slate-500 mt-2" data-id="element-160">Pago único</p>
              </div>

              <ul className="space-y-4 mb-10" data-id="element-161">
                {['Plantilla de Finanzas Personales (Google Sheets)', 'Gestión de Ingresos y Gastos', 'Control de Deudas y Ahorros', 'BONUS: Planner Financiero Imprimible', 'BONUS: Video Tutorial Paso a Paso', 'Acceso de por vida'].map((item, i) => <li key={i} className="flex items-start gap-3 text-slate-700" data-id="element-162">
                    <CheckCircle2 className="w-5 h-5 text-teal-500 flex-shrink-0 mt-0.5" data-id="element-163" />
                    <span className="text-sm md:text-base" data-id="element-164">{item}</span>
                  </li>)}
              </ul>

              <a href={CHECKOUT_URL} className="block w-full bg-teal-600 hover:bg-teal-700 text-white text-center font-bold py-4 rounded-xl text-lg transition-all shadow-lg hover:shadow-xl hover:-translate-y-1 mb-6" data-id="element-165">
                Acceder ahora por $9.90
              </a>

              <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-4" data-id="element-166">
                <a href="https://wa.link/ut4sjx" target="_blank" rel="noopener noreferrer" className="flex items-start gap-3 text-green-800 hover:text-green-900 transition-colors group" data-id="element-167">
                  <MessageCircle className="w-5 h-5 flex-shrink-0 mt-0.5 group-hover:scale-110 transition-transform" data-id="element-168" />
                  <div data-id="element-169">
                    <p className="font-semibold text-sm" data-id="element-170">
                      💬 ¿Tuviste algún problema con el pago o con el acceso?
                    </p>
                    <p className="text-sm" data-id="element-171">
                      Escribinos por WhatsApp y te ayudamos en minutos
                    </p>
                  </div>
                </a>
              </div>

              <div className="text-center text-xs text-slate-500 space-y-1" data-id="element-172">
                <p data-id="element-173">Producto digital – acceso inmediato.</p>
                <p data-id="element-174">
                  El acceso se envía automáticamente por email luego del pago.
                </p>
                <p data-id="element-175">No se envían productos físicos.</p>
              </div>

              <div className="flex items-center justify-center gap-4 text-slate-400 text-sm mt-6" data-id="element-176">
                <div className="flex items-center gap-1" data-id="element-177">
                  <ShieldCheck className="w-4 h-4" data-id="element-178" />
                  Pago seguro
                </div>
                <div className="flex items-center gap-1" data-id="element-179">
                  <Layout className="w-4 h-4" data-id="element-180" />
                  Entrega inmediata
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white" data-id="element-181">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-182">
          <div className="text-center mb-16" data-id="element-183">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-184">
              Lo que dicen nuestros usuarios
            </h2>
            <p className="text-lg text-slate-600" data-id="element-185">
              Miles de personas ya organizaron sus finanzas con esta plantilla
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-186">
            {[{
            name: 'María González',
            city: 'Ciudad de México, México',
            text: 'Antes no llegaba a fin de mes y no sabía por qué. Con esta plantilla pude ver exactamente dónde se iba mi plata. Ahora ahorro todos los meses.',
            rating: 5
          }, {
            name: 'Juan Martínez',
            city: 'Bogotá, Colombia',
            text: 'Súper fácil de usar. En 10 minutos ya tenía todo cargado y pude ver mi situación real. Me ayudó a salir de deudas en 6 meses.',
            rating: 5
          }, {
            name: 'Lucía Fernández',
            city: 'Lima, Perú',
            text: 'Lo mejor que hice este año. Ahora sé exactamente cuánto puedo gastar sin culpa. Me cambió la forma de ver el dinero.',
            rating: 5
          }, {
            name: 'Carlos Rodríguez',
            city: 'Santiago, Chile',
            text: 'Probé varias apps y ninguna me funcionó. Esta plantilla es simple, clara y hace exactamente lo que necesito. La recomiendo 100%.',
            rating: 5
          }, {
            name: 'Sofía López',
            city: 'Buenos Aires, Argentina',
            text: 'Tenía miedo de ver mis números reales, pero esta plantilla me dio tranquilidad. Ahora tengo un plan y sé que voy a cumplirlo.',
            rating: 5
          }, {
            name: 'Martín Díaz',
            city: 'Montevideo, Uruguay',
            text: 'Increíble la claridad que te da. Pude organizar mis deudas y armar un plan de ahorro realista. Vale cada centavo.',
            rating: 5
          }].map((testimonial, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="bg-slate-50 p-6 rounded-2xl border border-slate-200" data-id="element-187">
                <div className="flex gap-1 mb-4" data-id="element-188">
                  {[...Array(testimonial.rating)].map((_, j) => <Star key={j} className="w-5 h-5 fill-yellow-400 text-yellow-400" data-id="element-189" />)}
                </div>
                <p className="text-slate-700 mb-4 leading-relaxed" data-id="element-190">
                  "{testimonial.text}"
                </p>
                <div className="border-t border-slate-200 pt-4" data-id="element-191">
                  <p className="font-bold text-slate-900" data-id="element-192">{testimonial.name}</p>
                  <p className="text-sm text-slate-500" data-id="element-193">{testimonial.city}</p>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-white text-center" data-id="element-194">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-195">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6" data-id="element-196">
            Empezá hoy a construir tu tranquilidad financiera
          </h2>
          <p className="text-xl text-slate-600 mb-10" data-id="element-197">
            No dejes pasar otro mes sin saber a dónde va tu dinero. Ordená tus
            finanzas hoy mismo.
          </p>
          <a href={CHECKOUT_URL} className="inline-flex items-center justify-center gap-2 bg-teal-600 hover:bg-teal-700 text-white font-bold px-10 py-5 rounded-xl text-xl transition-all shadow-lg hover:shadow-xl hover:-translate-y-1" data-id="element-198">
            Empezar hoy por $9.90
            <ArrowRight className="w-6 h-6" data-id="element-199" />
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-50 py-12 border-t border-slate-200" data-id="element-200">
        <div className="max-w-7xl mx-auto px-4 text-center text-slate-500 text-sm" data-id="element-201">
          <p data-id="element-202">
            © {new Date().getFullYear()} Plantilla de Finanzas Personales.
            Todos los derechos reservados.
          </p>
          <p className="mt-2" data-id="element-203">
            Este sitio no forma parte del sitio web de Facebook o Facebook Inc.
          </p>
        </div>
      </footer>
    </div>;
}